<template>
    <div>
        <div class="modal fade" tabindex="-1" id="ModalEscolhaAssinatura" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-xl">
                <div class="modal-content border-0">
                    <div class="modal-body">
                        <div class="py-4 px-4">
                            <h3 class="title mb-1">
                                <i class="fas fa-users-cog me-2"></i>Permissões do Processo
                            </h3>
                            <hr />
                            <p class="text-muted mb-4">
                                <i class="fas fa-info-circle me-2"></i>Defina quem pode visualizar, realizar perguntas e enviar artefatos
                            </p>

                            <div class="d-flex align-items-start mb-4 flex-wrap gap-4">
                                <div class="d-flex align-items-start">
                                    <label class="me-3 mb-0 pt-1">
                                        <i class="fas fa-calendar-alt me-2"></i>Prazo geral
                                    </label>

                                    <div style="max-width: 260px;" class="flex-grow-1">
                                        <div class="d-flex align-items-center">
                                            <i class="fas fa-calendar-alt text-muted me-2" style="font-size: 0.9rem;"></i>
                                            <input type="date"
                                                   class="form-control form-control-sm prazo-input"
                                                   :class="{ 'border-danger': bloqueioPorPrazo }"
                                                   v-model="dataPrazo"
                                                   :min="hojeISO"
                                                   lang="pt-BR"
                                                   inputmode="numeric"
                                                   @input="limitarAnoPrazo" />
                                        </div>

                                        <!-- dica visual de erro no prazo -->
                                        <small v-if="bloqueioPorPrazo"
                                               class="text-danger d-block mt-1">
                                            {{ mensagemErroPrazo }}
                                        </small>
                                    </div>
                                </div>

                                <div class="d-flex align-items-start">
                                    <label class="me-3 mb-0 pt-1 text-nowrap">
                                        <i class="fas fa-signature me-2"></i>Tipo de assinatura
                                    </label>

                                    <div style="max-width: 320px;" class="flex-grow-1">
                                        <select class="form-select form-select-sm"
                                                :class="{ 'border-danger': bloqueioPorTipoAssinatura }"
                                                v-model="idTipoAssinatura"
                                                :disabled="tiposAssinatura.length === 0">
                                            <option :value="null" disabled>Selecione o tipo</option>
                                            <option v-for="t in tiposAssinatura" :key="t.idTipoAssinatura" :value="t.idTipoAssinatura">
                                                {{ t.nmTipoAssinatura }}
                                            </option>
                                        </select>
                                        <small v-if="bloqueioPorTipoAssinatura"
                                               class="text-danger d-block mt-1">
                                            Informe o tipo de assinatura.
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <!-- Bloco de seleção (Pessoa + Função) -->
                            <div class="selection-card mb-4">
                                <div class="row g-3 align-items-end">
                                    <div class="col-md-6">
                                        <label class="form-label">
                                            <i class="fas fa-user me-2"></i>Pessoa
                                        </label>
                                        <select class="form-select" v-model="selecionando.idPessoa" :disabled="availableAssinantes.length === 0">
                                            <option :value="null" disabled>Selecione uma pessoa</option>
                                            <option v-for="p in availableAssinantes" :key="p.idPessoa" :value="p.idPessoa">
                                                {{ p.nmPessoa }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="col-md-5">
                                        <label class="form-label">
                                            <i class="fas fa-briefcase me-2"></i>Função
                                        </label>
                                        <select class="form-select" v-model="selecionando.idTipoEnvolvido" :disabled="tiposEnvolvidos.length === 0">
                                            <option :value="null" disabled>Selecione a função</option>
                                            <option v-for="t in tiposEnvolvidos" :key="t.idTipoEnvolvido" :value="t.idTipoEnvolvido">
                                                {{ t.nmTipoEnvolvido }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="col-md-1 d-grid">
                                        <button class="btn btn-primary" :disabled="!podeAdicionar" @click="adicionarPessoa" title="Adicionar pessoa">
                                            <i class="fas fa-plus"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Lista montada -->
                            <div class="list-section">
                                <div class="d-flex align-items-center justify-content-between mb-3">
                                    <h6 class="section-title mb-0">
                                        <i class="fas fa-list me-2"></i>Pessoas adicionadas
                                    </h6>
                                    <span class="badge bg-primary rounded-pill" v-if="selecionados.length > 0">
                                        {{ selecionados.length }}
                                    </span>
                                </div>

                                <div v-if="selecionados.length === 0" class="empty-state">
                                    <i class="fas fa-users fa-3x mb-3 text-muted opacity-25"></i>
                                    <p class="text-muted mb-0">Nenhuma pessoa adicionada ainda</p>
                                    <small class="text-muted">Comece selecionando uma pessoa e função acima</small>
                                </div>

                                <div class="list-container">
                                    <div v-for="item in selecionados" :key="item.idPessoa" class="person-card">
                                        <div class="person-card-content">
                                            <div class="person-info">
                                                <div class="person-avatar">
                                                    <i class="fas fa-user-circle"></i>
                                                </div>
                                                <div class="person-details">
                                                    <div class="person-name">{{ item.nmPessoa }}</div>
                                                    <small class="person-login text-muted">{{ item.dsLogin }}</small>
                                                </div>
                                            </div>

                                            <div class="person-role">
                                                <label class="role-label">Função</label>
                                                <select class="form-select form-select-sm" v-model="item.idTipoEnvolvido" @change="sincronizarNomeFuncao(item)">
                                                    <option v-for="t in tiposEnvolvidos" :key="t.idTipoEnvolvido" :value="t.idTipoEnvolvido">
                                                        {{ t.nmTipoEnvolvido }}
                                                    </option>
                                                </select>
                                            </div>

                                            <div class="person-actions">
                                                <button class="action-btn action-btn-delete" @click="removerPessoa(item.idPessoa)" title="Remover pessoa">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                                                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div><!-- .modal-body -->

                    <button class="btn btn-md btn-icon btn-pill btn-white shadow position-absolute top-0 end-0 mt-n3 me-n3" data-bs-dismiss="modal">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x-lg" viewBox="0 0 16 16">
                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                        </svg>
                    </button>

                    <div class="modal-footer border-0">
                        <button class="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
                        <button class="btn btn-success"
                                :disabled="!podeSalvar || carregando"
                                @click="salvar">
                            <i class="fas fa-save me-2"></i>
                            <span v-if="!carregando">Enviar para Assinatura</span>
                            <span v-else>Enviando...</span>
                        </button>
                    </div>
                </div><!-- .modal-content -->
            </div><!-- .modal-dialog -->
        </div><!-- .modal -->
    </div>
</template>

<script>
    import { urlApi, avisoToast } from '@/config/global'
    import axios from 'axios'

    export default {
        name: 'ModalEscolhaAssinatura',
        props: {
            idProcessoArtefato: Number,
            nmModeloArtefato: String,
            nmProcesso: String,
            idProcessoModeloArtefato: Number,
        },
        data() {
            return {
                tiposEnvolvidos: [],
                tiposAssinatura: [],
                possiveisAssinantes: [],
                selecionando: {
                    idPessoa: null,
                    idTipoEnvolvido: null
                },
                selecionados: [],
                dataPrazo: null,
                idTipoAssinatura: null,
                myModalAlternative: null,
                carregando: false,
                idProcesso: null,
            }
        },
        computed: {
            availableAssinantes() {
                const idsSelecionados = new Set(this.selecionados.map(s => s.idPessoa))
                return this.possiveisAssinantes.filter(p => !idsSelecionados.has(p.idPessoa))
            },

            podeAdicionar() {
                return !!this.selecionando.idPessoa && !!this.selecionando.idTipoEnvolvido
            },

            idProcArtefato() {
                if (this.idProcessoArtefato != null) return this.idProcessoArtefato
                const fromRoute = parseInt(this.$route?.params?.id, 10)
                return Number.isNaN(fromRoute) ? null : fromRoute
            },

            hojeISO() {
                const hoje = new Date()
                hoje.setHours(0, 0, 0, 0)
                return hoje.toISOString().slice(0, 10)
            },

            prazoInvalido() {
                if (!this.dataPrazo) return false
                return this.dataPrazo < this.hojeISO
            },

            bloqueioPorPrazo() {
                if (this.selecionados.length === 0) return false
                if (!this.dataPrazo) return true
                if (this.prazoInvalido) return true
                return false
            },

            bloqueioPorTipoAssinatura() {
                if (this.selecionados.length === 0) return false
                return !this.idTipoAssinatura
            },

            mensagemErroPrazo() {
                if (this.selecionados.length === 0) return ''
                if (!this.dataPrazo) return 'Informe o prazo geral para enviar para assinatura.'
                if (this.prazoInvalido) return 'O prazo deve ser hoje ou uma data futura.'
                return ''
            },

            podeSalvar() {
                if (this.selecionados.length === 0) return false
                if (!this.dataPrazo) return false
                if (this.prazoInvalido) return false
                if (!this.idTipoAssinatura) return false
                return true
            },

            nmDocumento() {
                if (!this.nmProcesso && !this.nmModeloArtefato) return ''
                if (!this.nmProcesso) return this.nmModeloArtefato
                if (!this.nmModeloArtefato) return this.nmProcesso
                return `${this.nmProcesso} - ${this.nmModeloArtefato}`
            },
        },
        methods: {
            load() {
                this.getPossiveisAssinantes()
                this.getCargosAssinantes()
                this.getTiposAssinatura()
                this.getProcessoAssinatura()
            },

            getPossiveisAssinantes() {
                let $this = this;
                axios.get(`${urlApi}/Pessoa/GetPessoasAtivas`)
                    .then(res => {
                        if (res?.data?.success) {
                            const rows = res.data.data.result.rows || [];
                            $this.possiveisAssinantes = rows.map(p => ({
                                idPessoa: p.idPessoa,
                                nmPessoa: p.nmPessoa,
                                dsEmail: p.dsEmail,
                                dsLogin: p.dsLogin,
                                idPessoaInstituicaoCertificado: p.idPessoaInstituicaoCertificado,
                                idCertificadoSignGov: p.idCertificadoSignGov,
                            }));
                        } else {
                            console.warn('Resposta sem sucesso');
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao fazer requisição:', err);
                        avisoToast('Erro ao carregar processos!', 'warning');
                    });
            },

            getTiposAssinatura() {
                let $this = this;
                axios.get(`${urlApi}/TipoAssinatura/Get`)
                    .then(res => {
                        if (res?.data?.success) {
                            const rows = res.data.data.rows || [];
                            $this.tiposAssinatura = rows.map(r => {
                                const t = r.tipoAssinatura || r;
                                return {
                                    idTipoAssinatura: t.idTipoAssinatura,
                                    nmTipoAssinatura: t.nmTipoAssinatura,
                                    cdTipoAssinatura: t.cdTipoAssinatura,
                                };
                            });
                        } else {
                            console.warn('Resposta sem sucesso');
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao carregar tipos de assinatura:', err);
                        avisoToast('Erro ao carregar tipos de assinatura!', 'warning');
                    });
            },

            getProcessoAssinatura() {
                let $this = this;
                axios.get(`${urlApi}/ProcessoAssinatura/PrepararPorArtefato`, { params: { idProcessoArtefato: $this.idProcessoArtefato } })
                    .then(res => {
                        if (res?.data?.success) {
                            const rows = res.data.data.rows || [];
                            if (rows.length > 0) {
                                const item = rows[0];
                                $this.idProcesso = item.processo.idProcesso;
                            }
                        } else {
                            console.warn('Resposta sem sucesso');
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao fazer requisição:', err);
                        avisoToast('Erro ao carregar processos!', 'warning');
                    });
            },

            getCargosAssinantes() {
                let $this = this;
                axios.get(`${urlApi}/TipoEnvolvido/Get`)
                    .then(res => {
                        if (res?.data?.success) {
                            const rows = res.data.data.rows || [];
                            $this.tiposEnvolvidos = rows.map(r => {
                                const t = r.tipoEnvolvido || r; 
                                return {
                                    idTipoEnvolvido: t.idTipoEnvolvido,
                                    idCliente: t.idCliente ?? null,
                                    nmTipoEnvolvido: t.nmTipoEnvolvido,
                                    cdTipoEnvolvido: t.cdTipoEnvolvido,
                                    flPadrao: !!t.flPadrao,
                                    flAssinatura: !!t.flAssinatura,
                                    dtAlteracao: t.dtAlteracao,
                                    dsLoginInclusao: t.dsLoginInclusao,
                                    dsLoginAlteracao: t.dsLoginAlteracao,
                                    dtInclusao: t.dtInclusao,
                                };
                            });
                        } else {
                            console.warn('Resposta sem sucesso');
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao fazer requisição:', err);
                        avisoToast('Erro ao carregar processos!', 'warning');
                    });
            },

            adicionarPessoa() {
                if (!this.podeAdicionar) return

                const pessoa = this.possiveisAssinantes.find(p => p.idPessoa === this.selecionando.idPessoa)
                const func = this.tiposEnvolvidos.find(t => t.idTipoEnvolvido === this.selecionando.idTipoEnvolvido)
                if (!pessoa || !func) return

                this.selecionados.push({
                    idProcessoArtefato: this.idProcArtefato,
                    idPessoa: pessoa.idPessoa,
                    nmPessoa: pessoa.nmPessoa,
                    dsLogin: pessoa.dsLogin,
                    dsEmail: pessoa.dsEmail,
                    idPessoaInstituicaoCertificado: pessoa.idPessoaInstituicaoCertificado,
                    idCertificadoSignGov: pessoa.idCertificadoSignGov,
                    idTipoEnvolvido: func.idTipoEnvolvido,
                    nmTipoEnvolvido: func.nmTipoEnvolvido
                })

                this.selecionando.idPessoa = null
                this.selecionando.idTipoEnvolvido = null
            },

            removerPessoa(idPessoa) {
                this.selecionados = this.selecionados.filter(s => s.idPessoa !== idPessoa)
            },

            sincronizarNomeFuncao(item) {
                const f = this.tiposEnvolvidos.find(t => t.idTipoEnvolvido === item.idTipoEnvolvido)
                if (f) item.nmTipoEnvolvido = f.nmTipoEnvolvido
            },

            limitarAnoPrazo(event) {
                const value = event.target.value
                if (!value) return

                const partes = value.split('-')
                if (partes.length !== 3) return

                let [ano, mes, dia] = partes

                if (ano.length > 4) {
                    ano = ano.slice(0, 4)
                }

                const ajustado = `${ano}-${mes}-${dia}`
                if (ajustado !== this.dataPrazo) {
                    this.dataPrazo = ajustado
                }
            },

            async salvar() {
                if (!this.podeSalvar) {
                    if (this.selecionados.length === 0) {
                        avisoToast('Adicione pelo menos uma pessoa.', 'warning')
                    } else if (!this.dataPrazo) {
                        avisoToast('Informe o prazo geral.', 'warning')
                    } else if (this.prazoInvalido) {
                        avisoToast('O prazo deve ser hoje ou uma data futura.', 'warning')
                    } else if (!this.idTipoAssinatura) {
                        avisoToast('Informe o tipo de assinatura.', 'warning')
                    }
                    return
                }

                if (!this.idProcesso) {
                    avisoToast('Não foi possível identificar o processo. Recarregue a página.', 'error')
                    return
                }

                this.carregando = true

                try {
                    const idProcesso = this.idProcesso
                    const idProcessoArtefato = this.idProcArtefato
                    const dtLimiteAssinatura = this.dataPrazo
                    const nmDocumento = this.nmDocumento
                    const payloadEnvolvidos = this.selecionados.map(s => ({
                        idProcesso: idProcesso,
                        idPessoaInstituicaoCertificado: s.idPessoaInstituicaoCertificado,
                        idTipoEnvolvido: s.idTipoEnvolvido
                    }))

                    await axios.post(`${urlApi}/ProcessoAssinatura/SalvarEnvolvidos`, payloadEnvolvidos)

                    const payloadAssinatura = {
                        idProcesso: idProcesso,
                        idProcessoArtefato: idProcessoArtefato,
                        idProcessoModeloArtefato: this.idProcessoModeloArtefato,
                        dtLimiteAssinatura: dtLimiteAssinatura,
                        idTipoAssinatura: this.idTipoAssinatura,
                        nmDocumento: nmDocumento,
                        lstEmailsEnvolvidos: this.selecionados.map(s => s.dsEmail),
                        lstPapeisEnvolvidos: this.selecionados.map(s => s.nmTipoEnvolvido)
                    }

                    const res = await axios.post(`${urlApi}/ProcessoAssinatura/GeraProcessoAssinatura`, payloadAssinatura)

                    if (res?.data?.success) {
                        avisoToast('Processo enviado para assinatura com sucesso!', 'success')

                        const el = document.getElementById('ModalEscolhaAssinatura')
                        if (el && window.bootstrap) {
                            const modal = window.bootstrap.Modal.getInstance(el)
                            modal && modal.hide()
                        }

                        this.$emit('assinatura-gerada', res.data.data)
                    } else {
                        avisoToast(res?.data?.msg || 'Erro ao gerar processo de assinatura.', 'warning')
                    }
                } catch (err) {
                    console.error('Erro ao enviar para assinatura:', err)
                    avisoToast('Erro ao enviar para assinatura.', 'error')
                } finally {
                    this.carregando = false
                }
            },

            resetEstado() {
                this.selecionando.idPessoa = null
                this.selecionando.idTipoEnvolvido = null
                this.selecionados = []
                this.idTipoAssinatura = null
                this.dataPrazo = this.hojeISO  
            }
        },
        mounted() {
            const el = document.getElementById('ModalEscolhaAssinatura');
            if (!el) return;

            el.addEventListener('shown.bs.modal', () => {
                this.resetEstado();
                this.load();
            });

            el.addEventListener('hidden.bs.modal', () => {
                this.resetEstado();
            });
        },
        watch: {
            showModal(val) {
                if (!this.myModalAlternative) return
                if (val) {
                    this.myModalAlternative.show()
                    this.resetEstado()
                    this.load()
                } else {
                    this.myModalAlternative.hide()
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    /* Modal - Seguindo padrão do sistema */
    .modal-content {
        border-radius: 24px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    }

    .py-4.px-4 {
        padding: 1.5rem 2rem !important;
    }

    .title {
        font-size: 2rem;
        font-weight: 700;
        background: linear-gradient(135deg, #1f7bff 0%, #00d084 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        letter-spacing: -0.5px;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;

        i {
            background: linear-gradient(135deg, #1f7bff 0%, #00d084 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
    }

    hr {
        border: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent, #1f7bff, transparent);
        opacity: 0.3;
        margin: 0.75rem 0;
    }

    .selection-card {
        background: linear-gradient(135deg, rgba(248, 249, 250, 0.95) 0%, rgba(233, 236, 239, 0.95) 100%);
        border: 2px solid rgba(31, 123, 255, 0.15);
        border-radius: 14px;
        padding: 1.5rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;

        &:hover {
            border-color: rgba(31, 123, 255, 0.3);
            box-shadow: 0 8px 24px rgba(31, 123, 255, 0.15);
        }
    }

    .form-label {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;

        i {
            color: #1f7bff;
        }
    }

    .form-control,
    .form-select {
        border: 2px solid #e9ecef;
        border-radius: 10px;
        padding: 0.65rem 1rem;
        transition: all 0.3s ease;
        background-color: #ffffff;
        font-size: 0.95rem;

        &:focus {
            border-color: #1f7bff;
            box-shadow: 0 0 0 0.25rem rgba(31, 123, 255, 0.15);
            background-color: #ffffff;
        }

        &:hover:not(:focus) {
            border-color: #cbd5e0;
        }

        &:disabled {
            background-color: #f7fafc;
            cursor: not-allowed;
            opacity: 0.6;
        }
    }

    .btn {
        border-radius: 10px;
        font-weight: 600;
        padding: 0.65rem 1.25rem;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border: none;

        &:hover:not(:disabled) {
            transform: translateY(-2px);
        }

        &:active:not(:disabled) {
            transform: translateY(0);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .btn-primary {
        background: linear-gradient(135deg, #1f7bff 0%, #0056d2 100%);
        box-shadow: 0 4px 12px rgba(31, 123, 255, 0.3);

        &:hover:not(:disabled) {
            background: linear-gradient(135deg, #0056d2 0%, #003d99 100%);
            box-shadow: 0 8px 24px rgba(31, 123, 255, 0.4);
        }
    }

    .btn-success {
        background: linear-gradient(135deg, #00d084 0%, #00a065 100%);
        box-shadow: 0 4px 12px rgba(0, 208, 132, 0.3);
        color: white;

        &:hover:not(:disabled) {
            background: linear-gradient(135deg, #00bf7a 0%, #00d084 100%);
            box-shadow: 0 8px 24px rgba(0, 208, 132, 0.4);
            color: white;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .btn-secondary {
        background: #718096;
        color: white;

        &:hover {
            background: #4a5568;
            color: white;
        }
    }

    .btn-pill {
        border-radius: 50% !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        
        &:hover {
            transform: scale(1.1);
            background: linear-gradient(135deg, #dc3545 0%, #c82333 100%) !important;
            border-color: #dc3545 !important;
            box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
            
            svg {
                color: #ffffff;
            }
        }
        
        &:active {
            transform: scale(0.95);
        }
    }

    .list-section {
        margin-top: 1.5rem;
    }

    .section-title {
        font-weight: 700;
        color: #2d3748;
        font-size: 1.1rem;

        i {
            color: #1f7bff;
        }
    }

    .empty-state {
        text-align: center;
        padding: 3rem 2rem;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(248, 249, 250, 0.4) 100%);
        border-radius: 12px;
        border: 2px dashed #cbd5e0;
        
        i {
            color: #cbd5e0;
        }

        p {
            font-size: 1rem;
            font-weight: 500;
        }
        
        small {
            font-size: 0.85rem;
        }
    }

    .list-container {
        max-height: 400px;
        overflow-y: auto;
        padding-right: 0.5rem;
        
        &::-webkit-scrollbar {
            width: 8px;
        }
        
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }
        
        &::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #1f7bff 0%, #00d084 100%);
            border-radius: 10px;
            
            &:hover {
                background: linear-gradient(135deg, #0056d2 0%, #00a065 100%);
            }
        }
    }

    .person-card {
        background: white;
        border-radius: 12px;
        margin-bottom: 0.75rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        border: 1px solid #e9ecef;
        position: relative;
        
        &:hover {
            box-shadow: 0 8px 16px rgba(31, 123, 255, 0.15);
            transform: translateY(-2px);
            border-color: rgba(31, 123, 255, 0.3);
        }
    }

    .person-card-content {
        padding: 1rem 1.25rem;
        padding-right: 3.5rem;
        display: flex;
        align-items: center;
        gap: 1.5rem;
        flex-wrap: wrap;
    }

    .person-info {
        flex: 1;
        min-width: 200px;
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .person-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #1f7bff;
        flex-shrink: 0;
        font-size: 2rem;
    }

    .person-details {
        flex: 1;
    }

    .person-name {
        font-weight: 600;
        color: #2d3748;
        font-size: 1rem;
        margin-bottom: 0.25rem;
    }

    .person-login {
        font-size: 0.85rem;
        color: #718096;
    }

    .person-role {
        flex: 1;
        min-width: 200px;
    }

    .role-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
        display: block;
    }

    .person-actions {
        position: absolute;
        top: 0.75rem;
        right: 0.75rem;
        display: flex;
        align-items: center;
    }

    /* Botão de ação - Seguindo padrão do ProcessoInteligente */
    .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        border: 2px solid transparent;
        white-space: nowrap;
        position: relative;
        overflow: hidden;
    }

    .action-btn::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 220%;
        height: 220%;
        border-radius: 50%;
        background: rgba(255,255,255,0.30);
        transform: translate(-50%, -50%) scale(0);
        transition: transform .6s ease;
        pointer-events: none;
    }

    .action-btn:hover::before {
        transform: translate(-50%, -50%) scale(1);
    }

    .action-btn-delete {
        background: transparent;
        color: #ef4444;
        border-color: #ef4444;
    }

    .action-btn-delete::before {
        background: rgba(239, 68, 68, 0.1);
    }

    .action-btn-delete:hover {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
        border-color: #ef4444;
    }

    .action-btn:active {
        transform: translateY(0);
    }

    .badge {
        font-size: 0.85rem;
        padding: 0.5rem 1rem;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(31, 123, 255, 0.2);
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .person-card {
        animation: fadeIn 0.3s ease;
    }

    @media (max-width: 768px) {
        .py-4.px-4 {
            padding: 1rem 1.25rem !important;
        }

        .title {
            font-size: 1.5rem;
        }

        .person-card-content {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
            padding-right: 3.5rem;
        }
        
        .person-info,
        .person-role {
            min-width: 100%;
        }
        
        .person-actions {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
        }
    }
</style>
